import { AlertTriangle, FolderOpen, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import type { SmartAlert } from '@/hooks/useDashboardPremium'

interface DashboardAlertsSectionProps {
  alerts: SmartAlert[]
  activeProjects: number
  upcomingDeadlines: number
}

export function DashboardAlertsSection({
  alerts,
  activeProjects,
  upcomingDeadlines
}: DashboardAlertsSectionProps) {
  const navigate = useNavigate()

  return (
    <section className="grid gap-4 lg:grid-cols-3">
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="lg:col-span-2">
          <Card className="shadow-card h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                Alertas Inteligentes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    "flex items-start gap-3 p-4 rounded-xl border transition-colors",
                    alert.type === 'success' && "bg-success/5 border-success/20",
                    alert.type === 'warning' && "bg-warning/5 border-warning/20",
                    alert.type === 'danger' && "bg-destructive/5 border-destructive/20",
                    alert.type === 'info' && "bg-primary/5 border-primary/20"
                  )}
                >
                  <span className="text-xl flex-shrink-0">{alert.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "font-medium text-sm",
                      alert.type === 'success' && "text-success",
                      alert.type === 'warning' && "text-warning",
                      alert.type === 'danger' && "text-destructive",
                      alert.type === 'info' && "text-primary"
                    )}>
                      {alert.title}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">{alert.message}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Projects Quick View */}
      <div className={cn(alerts.length > 0 ? "lg:col-span-1" : "lg:col-span-3")}>
        <Card className="shadow-card h-full">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-primary" />
              Projetos Ativos
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-6">
            <p className="text-5xl font-display font-bold text-primary">{activeProjects}</p>
            <p className="text-muted-foreground mt-2">projetos em andamento</p>
            
            {upcomingDeadlines > 0 && (
              <Badge variant="outline" className="mt-4 text-warning border-warning/30">
                {upcomingDeadlines} com prazo em 7 dias
              </Badge>
            )}
            
            <Button 
              variant="outline" 
              className="w-full mt-6" 
              onClick={() => navigate('/projects')}
            >
              Ver Projetos
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
